import React, {CSSProperties, ReactNode, useEffect, useRef, useState} from 'react';
import {Choices, NameColumns, WideButtonRow} from '../../../shared/components/record/RecordKit';
import {PhotoViewer} from '../../../shared/components/PhotoViewer';
import {readSerialCandidates} from './equipment-catalog';
import './EquipmentKit.css';

/*
 * The pieces Android's equipment and Tools & Gear pages are built from
 * (EquipmentKit.kt, PillButton.kt, FloatingLabelField.kt, OptionRow.kt,
 * PhotoCapture.kt), in the web's record layout (SQ-574): choices keep their
 * phone size and wrap (Choices), a list of names wraps into columns
 * (NameColumns), and full-width buttons stop at about 420px (WideButtonRow).
 */

/** The page: one column of cards, at most the record's width. */
export const EquipmentPage = React.forwardRef<HTMLDivElement, {children: ReactNode}>(({children}, ref) => (
  <div ref={ref} className="eq">{children}</div>
));

export const PickerCard = ({children}: {children: ReactNode}) => <div className="eq-picker">{children}</div>;

export const ProfileCard = ({children}: {children: ReactNode}) => <div className="eq-card">{children}</div>;

export const CardDivider = () => <div className="eq-divider"/>;

export const StepTitle = ({children}: {children: ReactNode}) => <div className="eq-step-title">{children}</div>;

type PillTone = 'filled' | 'tonal' | 'danger';

/** Android's PillButton. A `wide` pill is full width on a phone and stops at about 420px, centred. */
export const Pill = ({label, tone = 'filled', compact, wide, onClick, disabled}: {
  label: string; tone?: PillTone; compact?: boolean; wide?: boolean; onClick: () => void; disabled?: boolean;
}) => {
  const button = (
    <button type="button" className={`eq-pill eq-pill--${tone}${compact ? ' eq-pill--compact' : ''}`}
            onClick={onClick} disabled={disabled}>
      {label}
    </button>
  );
  return wide ? <WideButtonRow>{button}</WideButtonRow> : button;
};

/** A step's title with its Back (or Cancel) pill on the right. */
export const StepHeader = ({title, action = 'Back', onAction}: {title: ReactNode; action?: string; onAction: () => void}) => (
  <div className="eq-row">
    <StepTitle>{title}</StepTitle>
    <Pill compact tone="tonal" label={action} onClick={onAction}/>
  </div>
);

/** A title with a compact tonal pill beside it (Change brand, Pick from the list). */
export const TitleWithPill = ({title, pill, onPill}: {title: ReactNode; pill: string; onPill: () => void}) => (
  <div className="eq-row">
    <StepTitle>{title}</StepTitle>
    <Pill compact tone="tonal" label={pill} onClick={onPill}/>
  </div>
);

export const ChoiceButton = ({label, selected, radio, style, onClick}: {
  label: string; selected?: boolean; radio?: boolean; style?: CSSProperties; onClick: () => void;
}) => (
  <button type="button" className={selected ? 'eq-choice eq-choice--on' : 'eq-choice'} style={style} onClick={onClick}
          aria-pressed={selected}>
    {radio && <span className={selected ? 'eq-radio eq-radio--on' : 'eq-radio'}/>}
    <span className="eq-choice-label">{label}</span>
  </button>
);

/** Android's ChoiceGrid: two across on a phone; wider, each keeps its phone size and the rows wrap. */
export const ChoiceGrid = ({children}: {children: ReactNode}) => <Choices>{children}</Choices>;

/** The way out of the catalog ("Not listed? Add your own"): a compact tonal pill, full width. */
export const EnterOwnButton = ({label, onClick}: {label: string; onClick: () => void}) => (
  <Pill compact wide tone="tonal" label={label} onClick={onClick}/>
);

export const CatalogNote = () => <div className="eq-small-note">Catalog still being refined. Missing? Enter your own.</div>;

/** The model list: a row per name with a ›, thin lines between rows and columns; columns on a wide page. */
export const NameList = ({names, empty, onPick}: {names: string[]; empty: string; onPick: (index: number) => void}) => (
  <div className="eq-names">
    {names.length === 0 ? <div className="eq-names-empty">{empty}</div> : (
      <NameColumns>
        {names.map((name, index) => (
          <button type="button" key={`${index}-${name}`} className="eq-name" onClick={() => onPick(index)}>
            <span>{name}</span><span className="eq-name-chevron" aria-hidden="true">›</span>
          </button>
        ))}
      </NameColumns>
    )}
  </div>
);

export const LinkText = ({label, onClick}: {label: string; onClick: () => void}) => (
  <button type="button" className="eq-link" onClick={onClick}>{label}</button>
);

/** FieldHeading: "Nickname (optional)" over a field. */
export const FieldHeading = ({text, optional}: {text: string; optional?: boolean}) => (
  <span className="eq-field-heading">{text}{optional && <span className="eq-soft"> (optional)</span>}</span>
);

/**
 * A field in Android's green-edged box: with [heading] above it (the
 * profile's FieldHeading), or with [floating], Android's FloatingLabelField
 * label sitting on the border (the enter-your-own cards).
 */
export const Field = ({heading, floating, optional, value, placeholder, onChange, maxLength = 100}: {
  heading?: string; floating?: string; optional?: boolean; value: string; placeholder?: string;
  onChange: (v: string) => void; maxLength?: number;
}) => (
  <label className={floating ? 'eq-field eq-field--floating' : 'eq-field'}>
    {heading && <FieldHeading text={heading} optional={optional}/>}
    {floating && <span className="eq-float-label">{floating}</span>}
    <input className="eq-input" value={value} placeholder={placeholder} maxLength={maxLength}
           aria-label={heading || floating ? undefined : placeholder} onChange={e => onChange(e.target.value)}/>
  </label>
);

/** Android's TypeSelect: a dropdown box. `compact` is the profile's (Year), which can be cleared. */
export const Select = ({title, optional, value, placeholder, options, onChange, compact}: {
  title?: string; optional?: boolean; value?: string; placeholder: string; options: {value: string; label: string}[];
  onChange: (v: string) => void; compact?: boolean;
}) => (
  <label className="eq-field">
    {title && (compact ? <FieldHeading text={title} optional={optional}/> : <span className="eq-select-title">{title}</span>)}
    <select className={`eq-select${compact ? ' eq-select--compact' : ''}${value ? '' : ' eq-select--empty'}`}
            value={value ?? ''} onChange={e => onChange(e.target.value)}>
      <option value="" disabled={!compact}>{placeholder}</option>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

/** CheckOptionRow with Android's GreenCheckbox. */
export const CheckRow = ({label, checked, onChange}: {label: string; checked: boolean; onChange: (v: boolean) => void}) => (
  <button type="button" className="eq-check" role="checkbox" aria-checked={checked} onClick={() => onChange(!checked)}>
    <span className={checked ? 'eq-gcheck eq-gcheck--on' : 'eq-gcheck'} aria-hidden="true">{checked && '✓'}</span>
    {label}
  </button>
);

/** Android's stock Material3 AlertDialog, as the delete confirm uses it. */
export const ConfirmDialog = ({title, body, cancel, confirm, onCancel, onConfirm}: {
  title: string; body?: string; cancel: string; confirm: string; onCancel: () => void; onConfirm: () => void;
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return (
    <div className="eq-scrim" onClick={onCancel}>
      <div className="eq-dialog" role="alertdialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="eq-dialog-title">{title}</div>
        {body && <div className="eq-dialog-body">{body}</div>}
        <div className="eq-dialog-actions">
          <button type="button" className="eq-dialog-cancel" onClick={onCancel}>{cancel}</button>
          <button type="button" className="eq-dialog-confirm" onClick={onConfirm}>{confirm}</button>
        </div>
      </div>
    </div>
  );
};

/**
 * Android's MoneyStepper: − , a typeable dollar amount, + . The field holds
 * the text as typed (moneyInput); [onStep] moves it a dollar, repeating while
 * held. "$ 0" is grey only when empty.
 */
export const MoneyStepper = ({title, value, onChange, onStep}: {
  title: string; value: string; onChange: (v: string) => void; onStep: (up: boolean) => void;
}) => {
  const timer = useRef<number>();
  const step = useRef(onStep);
  step.current = onStep;
  const release = () => window.clearTimeout(timer.current);
  useEffect(() => release, []);
  const hold = (up: boolean) => {
    // A second press before the first lets go (two fingers, pen and touch) must not leave a chain no release reaches.
    release();
    step.current(up);
    let count = 0;
    const again = () => {
      count++;
      step.current(up);
      timer.current = window.setTimeout(again, count < 10 ? 110 : count < 30 ? 50 : 20);
    };
    timer.current = window.setTimeout(again, 400);
  };
  const button = (up: boolean) => (
    <button type="button" aria-label={`${up ? 'More' : 'Less'} ${title}`}
            onPointerDown={() => hold(up)} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); step.current(up); } }}>
      {up ? '+' : '−'}
    </button>
  );
  return (
    <div className="eq-stepper-wrap">
      <div className="eq-stepper-title">{title}</div>
      <div className="eq-stepper">
        {button(false)}
        <span className="eq-stepper-value">
          <span className={value ? '' : 'eq-soft'}>$</span>
          <input className={value ? 'eq-stepper-input' : 'eq-stepper-input eq-soft'} inputMode="decimal" value={value}
                 placeholder="0" aria-label={title} onChange={e => onChange(e.target.value)}/>
        </span>
        {button(true)}
      </div>
    </div>
  );
};

export const PageMessage = ({children}: {children: ReactNode}) => <div className="eq-message">{children}</div>;

export const ErrorText = ({children}: {children: ReactNode}) => <div className="eq-error" role="alert">{children}</div>;

/**
 * "VIN / serial number" with a picture of the plate (PhotoCapture.kt's
 * SerialNumberField). A new picture uploads at once (the page attaches it on
 * Save) and shows as a thumbnail that opens full size. The browser reads the
 * picture and offers what looks like the number with "Use this number"; it
 * never fills the box by itself, since a misread plate should not slip in
 * unseen.
 */
export const SerialNumberField = ({heading, value, onChange, photoUrl, uploading, onPhoto, onRemovePhoto, placeholder}: {
  heading: string; value: string; onChange: (v: string) => void;
  /** The stored picture's URL, or a local preview while it uploads. */
  photoUrl?: string; uploading: boolean; onPhoto: (file: File) => void; onRemovePhoto: () => void; placeholder?: string;
}) => {
  const input = useRef<HTMLInputElement>(null);
  const [reading, setReading] = useState(false);
  const [candidates, setCandidates] = useState<string[]>();
  const [viewing, setViewing] = useState(false);
  const run = useRef(0);

  const pick = () => input.current?.click();
  const picked = (file?: File) => {
    if (!file) return;
    const mine = ++run.current;
    setCandidates(undefined);
    setReading(true);
    onPhoto(file);
    void readSerialCandidates(file).then(found => {
      if (run.current !== mine) return;
      setReading(false);
      setCandidates(found);
    });
  };

  return (
    <div className="eq-serial">
      <div className="eq-field">
        <div className="eq-serial-head">
          <FieldHeading text={heading} optional/>
          {!photoUrl && <button type="button" className="eq-photo-chip" onClick={pick}>📷 Photo</button>}
        </div>
        <input className="eq-input" value={value} placeholder={placeholder} maxLength={100} aria-label={heading}
               onChange={e => onChange(e.target.value)}/>
      </div>
      <input ref={input} type="file" accept="image/*" hidden data-testid="serial-photo-input"
             onChange={e => { picked(e.target.files?.[0]); e.target.value = ''; }}/>
      {photoUrl && (
        <div className="eq-thumb-row">
          <button type="button" className="eq-thumb" onClick={() => setViewing(true)} aria-label="Picture of the serial plate">
            <img src={photoUrl} alt=""/>
            {uploading && <span className="eq-thumb-spinner" aria-hidden="true"/>}
          </button>
          {!uploading && <>
            <LinkText label="Retake" onClick={pick}/>
            <LinkText label="Remove" onClick={() => { run.current++; setReading(false); setCandidates(undefined); onRemovePhoto(); }}/>
          </>}
        </div>
      )}
      {reading ? (
        <div className="eq-reading"><span className="eq-spinner" aria-hidden="true"/>Reading the number…</div>
      ) : candidates && (
        <div className="eq-read">
          {candidates.length === 0 ? <>
            <div className="eq-read-note">
              Couldn't read a number from this picture. Type it in, or retake it closer and in good light.
            </div>
            <div><Pill compact tone="tonal" label="OK" onClick={() => setCandidates(undefined)}/></div>
          </> : <>
            <div className="eq-read-note">
              {candidates.length === 1 ? 'We read this number. Check it against the plate:' : 'We read these. Pick the right one:'}
            </div>
            {candidates.map(number => (
              <div key={number} className="eq-row">
                <span className="eq-read-number">{number}</span>
                {number === value
                  ? <span className="eq-read-in-use">In use</span>
                  : <Pill compact label="Use this number" onClick={() => { onChange(number); setCandidates(undefined); }}/>}
              </div>
            ))}
            <div><button type="button" className="eq-text-button" onClick={() => setCandidates(undefined)}>Not right, I'll type it</button></div>
          </>}
        </div>
      )}
      {viewing && photoUrl && (
        <PhotoViewer photos={[{id: 'serial', url: photoUrl}]} startIndex={0} onClose={() => setViewing(false)}
                     label="Serial plate"/>
      )}
    </div>
  );
};
